// eslint-disable-next-line @github-ui/github-monorepo/filename-convention, import/no-namespace
import * as Octicons from '@primer/octicons-react'
import type {ColorMode} from '@primer/react-brand'
import {OS} from '@github-ui/get-os'
import {isFeatureEnabled} from '@github-ui/feature-flags'

import {COUNTRIES_LIST} from '../../../../lib/data/countries'
import type {IconName} from './svgs'

import ImageVisualDesktop from './assets/images/visual_desktop.webp'
import ImageVisualMobile from './assets/images/visual_mobile.webp'

export interface HeroVisualData {
  src: HeroVisualSrc
  alt: string
}
export type HeroVisualSrc = Record<Extract<OS, 'macOS' | 'Windows'>, Record<Exclude<ColorMode, 'auto'>, string>>
export type HeroVisual = Record<'desktop' | 'mobile', HeroVisualData>
export type HeroVisualResolved = Record<'desktop' | 'mobile', Omit<HeroVisualData, 'src'> & {src: string}>

export interface GitHubAppWaitlistPageCopy {
  hero: {
    label: string
    headline: string
    osBadges: Array<{
      iconName: IconName
      label: string
    }>
    body: string
    cta: {
      label: string
      href: string
    }
    trailingComponent?: string
    visual: HeroVisual
  }
  content: {
    heading: string
    features: Array<{
      iconName: Exclude<keyof typeof Octicons, 'default'>
      heading: string
      body: string
    }>
  }
  form: {
    anchorId: string
    heading: string
    requirementMessage: string
    disclaimer: string
  }
  thankYou: {
    heading: string
    body: string
  }
}

export const getCopy = (): GitHubAppWaitlistPageCopy => {
  const isGitHubAppLpLive = isFeatureEnabled('site_github_app_ga_page')

  return {
    hero: {
      label: 'Preview',
      headline: 'From issue to merge, in one app',
      osBadges: [
        {iconName: 'apple', label: 'MacOS'},
        {iconName: 'windows', label: 'Windows'},
        {iconName: 'linux', label: 'Linux'},
      ],
      body: isGitHubAppLpLive
        ? 'Join the waitlist to stay informed and get notified when we expand access to the GitHub Copilot app technical preview—a new desktop experience for agent-driven development built natively on GitHub.'
        : 'Join the waitlist for early access to the GitHub Copilot app, a new desktop experience for agent-driven development built natively on GitHub.',
      cta: {
        label: 'Sign up for the waitlist',
        href: '#form',
      },
      // Markdown
      trailingComponent: isGitHubAppLpLive
        ? 'Already a Copilot Pro, Pro+, Max, Business, or Enterprise customer? You can immediately [install the app and get started today](https://gh.io/github-copilot-app-repo).'
        : 'On a Copilot Business or Enterprise plan?  \nYou can [access the app today](https://gh.io/github-copilot-app-repo).',
      visual: {
        desktop: {
          src: {
            [OS.macOS]: {
              light: ImageVisualDesktop,
              dark: ImageVisualDesktop,
            },
            [OS.Windows]: {
              light: ImageVisualDesktop,
              dark: ImageVisualDesktop,
            },
          },
          alt: 'Preview of the GitHub desktop app showing a pull request review with AI-organized findings.',
        },
        mobile: {
          src: {
            [OS.macOS]: {
              light: ImageVisualMobile,
              dark: ImageVisualMobile,
            },
            [OS.Windows]: {
              light: ImageVisualMobile,
              dark: ImageVisualMobile,
            },
          },
          alt: 'Preview of the GitHub desktop app showing a pull request review with AI-organized findings.',
        },
      },
    },
    content: {
      heading: 'What you get in the technical preview',
      features: [
        {
          iconName: 'MarkGithubIcon',
          heading: 'The full dev lifecycle',
          body: 'Pick up an issue or PR from your inbox, put agents on it, review the diff, and merge. Or let an agent close the loop for you.',
        },
        {
          iconName: 'ArrowSwitchIcon',
          heading: 'Parallel agents, fully in view',
          body: 'Run multiple agent sessions across repos, each isolated and tracked in real time.',
        },
        {
          iconName: 'WorkflowIcon',
          heading: 'Built to adapt to how you work',
          body: 'Automate recurring workflows and extend agents with MCP servers and your own custom skills.',
        },
      ],
    },
    form: {
      anchorId: 'form',
      heading: 'Tell us about yourself, and we’ll see if you’re a good fit for the preview.',
      // Markdown
      requirementMessage: '__\\*__ All fields marked with an asterisk are required.',
      // Markdown
      disclaimer: isGitHubAppLpLive
        ? '\\* The GitHub Copilot app is currently only available to existing Copilot Pro, Pro+, Max, Business and Enterprise users. Access for Copilot Free and new Copilot customers coming soon.  \n\\*\\* Copilot Business and Enterprise availability requires enterprise/organization-level opt-in for preview and Copilot CLI enablement.'
        : '\\* The GitHub Copilot app requires an active paid GitHub Copilot subscription.  \n\\*\\* Copilot Business and Enterprise availability requires enterprise/organization-level opt-in for preview and Copilot CLI enablement.',
    },
    thankYou: {
      heading: 'You are now on the waitlist',
      body: 'Admission to try the pre-release version of the product will be limited. We will be in contact with updates.',
    },
  }
}

export interface GitHubAppWaitlistPageFormData {
  eloqua: {
    elqFormName: string
    source: string
    endpoint: string
    elqSiteId: string
  }
  rows: Array<{
    heading?: string
    fields: Array<{
      name: string
      label: string
      type: 'text' | 'email' | 'tel' | 'url'
      required?: boolean
      placeholder?: string
    }>
  }>
  country: {
    name: string
    label: string
    options: Array<{label: string; value: string}>
    placeholder: string
  }
  consent: {
    fieldName: string
    fieldValue: string
    countryFieldSelectorId: string
    exampleFields: string[]
    privacyStatementHref: string
    emailSubscriptionSettingsLinkHref: string
    invalidMessage: string
  }
  submitText: string
}

export const FORM_DATA: GitHubAppWaitlistPageFormData = {
  eloqua: {
    elqFormName: 'GitHubAppWaitlist-10462',
    source: 'GitHubAppWaitlist-10462',
    elqSiteId: '88570519',
    endpoint: 'https://s88570519.t.eloqua.com/e/f2',
  },
  rows: [
    {
      fields: [
        {name: 'firstName', label: 'First name', type: 'text', required: true},
        {name: 'lastName', label: 'Last name', type: 'text', required: true},
      ],
    },
    {
      fields: [
        {name: 'company', label: 'Company Name', type: 'text', placeholder: 'Acme, Inc.', required: true},
        {
          name: 'emailAddress',
          type: 'email',
          label: 'Email Address',
          placeholder: 'your.name@example.com',
          required: true,
        },
      ],
    },
    {
      fields: [
        {name: 'jobTitle', label: 'Job title', type: 'text', placeholder: 'e.g. Engineering Manager', required: true},
        {name: 'gitHubLogin1', label: 'GitHub handle', type: 'text', placeholder: 'e.g. @monalisa', required: true},
      ],
    },
    {
      heading: 'Where are you active in the developer community online? (optional)',
      fields: [
        {name: 'xTwitter', label: 'X (Twitter handle)', type: 'text'},
        {name: 'linkedIn', label: 'LinkedIn profile', type: 'text'},
      ],
    },
    {
      fields: [
        {name: 'blog', label: 'Blog / personal website', type: 'text'},
        {name: 'youtubeOrTwitch', label: 'YouTube or Twitch Channel', type: 'text'},
      ],
    },
  ],
  country: {
    name: 'country',
    label: 'Country',
    options: COUNTRIES_LIST,
    placeholder: 'Choose your country/region',
  },
  consent: {
    fieldName: 'marketing_email_opt_in',
    fieldValue: 'optInExplicit',
    countryFieldSelectorId: 'country',
    exampleFields: ['first name', 'last name', 'company', 'email'],
    privacyStatementHref: 'https://docs.github.com/articles/github-privacy-statement',
    emailSubscriptionSettingsLinkHref: '/settings/emails/subscriptions/link-request/new',
    invalidMessage: 'You need to accept the required checkboxes to continue.',
  },
  submitText: 'Sign up',
}

export const COLOR_MODE: ColorMode = 'light'
